import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PackageCheck, Undo2, Loader2, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Delivered-Excel batch history + one-click undo.
 *
 * Each row summarises a single delivered-excel import batch:
 *   • Timestamp + uploader
 *   • Groups touched
 *   • "N cards / M flipped" counts
 *   • Undo button that soft-deletes new cards AND un-flips patched
 *     entries back to pending (server-side atomic).
 *
 * The panel is collapsed by default so it doesn't crowd the LPG Report
 * page — admins expand it only when they actually need to undo.
 */
export default function DeliveredBatchesPanel({ onUndone }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [err, setErr] = useState("");
  const [undoing, setUndoing] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/bulk-paste/delivered-batches?limit=30");
      setBatches(data.batches || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) load();
  }, [expanded, load]);

  const handleUndo = async (batchId, summary) => {
    if (
      !window.confirm(
        `Undo this delivered import?\n\n${summary}\n\nNew cards will be soft-deleted and any pending entries this batch flipped will revert to pending. This cannot be undone from the UI.`
      )
    ) {
      return;
    }
    setUndoing(batchId);
    try {
      const { data } = await api.post(
        `/bulk-paste/delivered-batches/${batchId}/undo`
      );
      toast.success(
        `Undone · ${data.cards_deleted} card(s) deleted · ${data.entries_reverted} entry/entries reverted to pending.`
      );
      await load();
      if (onUndone) onUndone(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUndoing("");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="bg-white border border-[#DCDCD3] rounded-lg"
      data-testid="delivered-batches-panel"
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="delivered-batches-toggle"
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-[#F5F5F0] transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-[#6B6B61]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#6B6B61]" />
          )}
          <PackageCheck className="w-4 h-4 text-[#385C4B]" />
          <span
            className="text-sm font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Delivered-Excel Batches
          </span>
          <span className="text-xs text-[#6B6B61]">
            (undo a wrong-file upload — reverts new cards + flips back to pending)
          </span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-[#DCDCD3] p-3 space-y-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#6B6B61]">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading batches…
            </div>
          )}
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {err}
            </div>
          )}
          {!loading && !err && batches.length === 0 && (
            <div className="text-sm text-[#6B6B61] italic px-2 py-3">
              Koi delivered-excel batch nahi mila. Bulk Paste page pe
              "Delivered Excel…" button use karke import karo — yaha
              history + undo options appear ho jayenge.
            </div>
          )}
          {batches.map((b) => {
            const summary = `${b.card_count} card${b.card_count === 1 ? "" : "s"} · ${b.flip_count} flipped · ${(b.group_names || []).join(", ") || "no group"}`;
            return (
              <div
                key={b.batch_id}
                data-testid={`delivered-batch-row-${b.batch_id}`}
                className="border border-[#DCDCD3] rounded p-2 flex flex-wrap items-center gap-3 text-sm"
              >
                <div className="flex-1 min-w-[220px]">
                  <div className="font-mono text-xs text-[#6B6B61]">
                    {fmtDate(b.created_at)}
                  </div>
                  <div className="text-[#1C1C19]">
                    <b>{b.uploaded_by || "—"}</b>{" "}
                    <span className="text-[#6B6B61]">
                      · {(b.group_names || []).join(", ") || "—"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-[#385C4B] font-semibold">
                    {b.card_count} card{b.card_count === 1 ? "" : "s"}
                  </span>
                  <span className="text-blue-700 font-semibold">
                    {b.flip_count} flipped
                  </span>
                  <span className="text-[#6B6B61]">
                    {b.card_lpg_count} entries
                  </span>
                </div>
                <button
                  onClick={() => handleUndo(b.batch_id, summary)}
                  disabled={undoing === b.batch_id}
                  data-testid={`delivered-batch-undo-${b.batch_id}`}
                  className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-semibold hover:bg-red-700 disabled:bg-[#DCDCD3] disabled:text-[#6B6B61] flex items-center gap-1.5"
                >
                  {undoing === b.batch_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Undo2 className="w-3 h-3" />
                  )}
                  Undo
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
